/**
 * Deterministic, offline grammar checker — the free-tier "writing assistant"
 * competitors gate behind accounts, built the CitationEasy way instead: pure
 * string-in → findings-out, no AI, no network, nothing uploaded.
 *
 * Philosophy matches `citation-check.ts`: every rule is high-precision (a false
 * positive is worse than a miss), every finding is advisory, and the module is
 * self-contained — it never touches the citation core.
 */

export type GrammarConfidence = "high" | "medium";

export interface GrammarFinding {
  /** Stable rule id (React key / tests). */
  rule: string;
  /** Short human-readable label. */
  title: string;
  /** What looks off and why. */
  message: string;
  /** The offending text, as matched. */
  excerpt: string;
  /** Copyable replacement, when one is safe to offer. */
  suggestion?: string;
  /** Character offset of the match in the input. */
  index: number;
  confidence: GrammarConfidence;
}

// --- Rule: doubled words --------------------------------------------------------

const RE_DOUBLED_WORD = /\b([A-Za-z]+)\s+\1\b/gi;
// Legitimate doublings ("had had", "that that") — skip.
const DOUBLED_ALLOWLIST = new Set(["had", "that", "can", "do", "so"]);

function checkDoubledWords(text: string): GrammarFinding[] {
  const findings: GrammarFinding[] = [];
  for (const match of text.matchAll(RE_DOUBLED_WORD)) {
    const word = match[1].toLowerCase();
    if (DOUBLED_ALLOWLIST.has(word)) {
      continue;
    }
    findings.push({
      rule: "doubled-word",
      title: "Repeated word",
      message: `"${match[1]}" appears twice in a row.`,
      excerpt: match[0],
      suggestion: match[1],
      index: match.index,
      confidence: "high",
    });
  }
  return findings;
}

// --- Rule: a/an agreement -------------------------------------------------------

// "a" + vowel-initial word (likely should be "an"), except vowel letters that are
// pronounced with a consonant sound.
const RE_A_BEFORE_VOWEL = /\ba (?=[aeiouAEIOU][a-z]*\b)([a-z]+)/g;
const A_EXCEPTIONS_PREFIXES = [
  "uni",
  "use",
  "user",
  "usu",
  "eu",
  "one",
  "once",
  "ubiq",
  "ute",
  "uter",
  "ukulele",
  "uran",
  "urin", // "a urinal" — you-sound
  "utopia",
];
// "an" + consonant-initial word (likely should be "a"), except silent-h words and
// letter-name abbreviations (an MRI, an FBI agent, an SEO plan, an X-ray…).
const RE_AN_BEFORE_CONSONANT = /\ban (?=[bcdfgjklmnpqstvwyz][a-z]*\b)([a-z]+)/g;
const AN_EXCEPTIONS_PREFIXES = [
  "honest",
  "honor",
  "honour",
  "hour",
  "heir",
  "homage",
];

function startsWithAny(word: string, prefixes: string[]): boolean {
  return prefixes.some((prefix) => word.startsWith(prefix));
}

function checkArticles(text: string): GrammarFinding[] {
  const findings: GrammarFinding[] = [];
  for (const match of text.matchAll(RE_A_BEFORE_VOWEL)) {
    const word = match[1];
    if (startsWithAny(word, A_EXCEPTIONS_PREFIXES)) {
      continue;
    }
    findings.push({
      rule: "a-before-vowel",
      title: "Article: a → an",
      message: `Use "an" before a vowel sound: "an ${word}".`,
      excerpt: `a ${word}`,
      suggestion: `an ${word}`,
      index: match.index,
      confidence: "medium",
    });
  }
  for (const match of text.matchAll(RE_AN_BEFORE_CONSONANT)) {
    const word = match[1];
    if (startsWithAny(word, AN_EXCEPTIONS_PREFIXES)) {
      continue;
    }
    findings.push({
      rule: "an-before-consonant",
      title: "Article: an → a",
      message: `Use "a" before a consonant sound: "a ${word}".`,
      excerpt: `an ${word}`,
      suggestion: `a ${word}`,
      index: match.index,
      confidence: "medium",
    });
  }
  return findings;
}

// --- Rule: punctuation spacing --------------------------------------------------

const RE_SPACE_BEFORE_PUNCT = /[A-Za-z] ([,.;:!?])/g;
const RE_NO_SPACE_AFTER_COMMA = /[A-Za-z],[A-Za-z]/g;
const RE_REPEATED_PUNCT = /([!?,;:])\1+/g;
const RE_DOUBLE_SPACE = /[A-Za-z][ ]{2,}[A-Za-z]/g;

function checkPunctuation(text: string): GrammarFinding[] {
  const findings: GrammarFinding[] = [];
  for (const match of text.matchAll(RE_SPACE_BEFORE_PUNCT)) {
    findings.push({
      rule: "space-before-punctuation",
      title: "Space before punctuation",
      message: `Remove the space before "${match[1]}".`,
      excerpt: match[0],
      index: match.index,
      confidence: "high",
    });
  }
  for (const match of text.matchAll(RE_NO_SPACE_AFTER_COMMA)) {
    findings.push({
      rule: "no-space-after-comma",
      title: "Missing space after comma",
      message: "Add a space after the comma.",
      excerpt: match[0],
      index: match.index,
      confidence: "high",
    });
  }
  for (const match of text.matchAll(RE_REPEATED_PUNCT)) {
    findings.push({
      rule: "repeated-punctuation",
      title: "Repeated punctuation",
      message: `"${match[0]}" — one "${match[1]}" is enough in academic writing.`,
      excerpt: match[0],
      suggestion: match[1],
      index: match.index,
      confidence: "high",
    });
  }
  for (const match of text.matchAll(RE_DOUBLE_SPACE)) {
    findings.push({
      rule: "double-space",
      title: "Double space",
      message: "Multiple spaces between words — use one.",
      excerpt: match[0],
      index: match.index,
      confidence: "high",
    });
  }
  return findings;
}

// --- Rule: common confusions & misspellings -------------------------------------

interface Confusion {
  regex: RegExp;
  title: string;
  message: string;
  suggestion: (match: RegExpMatchArray) => string;
  confidence: GrammarConfidence;
}

const CONFUSIONS: Confusion[] = [
  {
    regex: /\b(should|could|would|must|might) of\b/gi,
    title: '"of" after a modal verb',
    message: 'The contraction of "have" sounds like "of" — write "have".',
    suggestion: (m) => `${m[1]} have`,
    confidence: "high",
  },
  {
    regex: /\balot\b/gi,
    title: '"alot"',
    message: '"alot" is not a word — write "a lot".',
    suggestion: () => "a lot",
    confidence: "high",
  },
  {
    regex: /\birregardless\b/gi,
    title: '"irregardless"',
    message: 'Nonstandard — write "regardless".',
    suggestion: () => "regardless",
    confidence: "high",
  },
  {
    regex: /\bfor all intensive purposes\b/gi,
    title: "Eggcorn",
    message: 'The idiom is "for all intents and purposes".',
    suggestion: () => "for all intents and purposes",
    confidence: "high",
  },
  {
    regex: /\bits (a|an|been)\b/gi,
    title: '"its" vs "it\'s"',
    message: `Here "its" is the contraction of "it is/has" — write "it's".`,
    suggestion: (m) => `it's ${m[1]}`,
    confidence: "medium",
  },
  {
    regex: /\byour welcome\b/gi,
    title: '"your" vs "you\'re"',
    message: `"You are welcome" — write "you're welcome".`,
    suggestion: () => "you're welcome",
    confidence: "high",
  },
  {
    regex: /\b(more|less|better|worse|rather|other|greater|fewer) then\b/gi,
    title: '"then" vs "than"',
    message: 'Comparisons take "than", not "then".',
    suggestion: (m) => `${m[1]} than`,
    confidence: "high",
  },
];

const MISSPELLINGS: Record<string, string> = {
  definately: "definitely",
  seperate: "separate",
  recieve: "receive",
  occured: "occurred",
  untill: "until",
  wich: "which",
  teh: "the",
  becuase: "because",
  accomodate: "accommodate",
  existance: "existence",
  enviroment: "environment",
  goverment: "government",
  publically: "publicly",
  arguement: "argument",
  beleive: "believe",
  concious: "conscious",
  occurence: "occurrence",
  neccessary: "necessary",
  succesful: "successful",
  tommorow: "tomorrow",
  wierd: "weird",
  acheive: "achieve",
  athiest: "atheist",
  calender: "calendar",
  collegue: "colleague",
  commitee: "committee",
  concensus: "consensus",
  embarass: "embarrass",
  gaurantee: "guarantee",
  independant: "independent",
  liason: "liaison",
  maintainance: "maintenance",
  mispell: "misspell",
  noticable: "noticeable",
  occassion: "occasion",
  persistant: "persistent",
  priviledge: "privilege",
  reccomend: "recommend",
  refered: "referred",
  relevent: "relevant",
};

const RE_WORDS = /\b[A-Za-z]+\b/g;

function matchCase(replacement: string, original: string): string {
  if (original[0] === original[0].toUpperCase()) {
    return replacement[0].toUpperCase() + replacement.slice(1);
  }
  return replacement;
}

function checkConfusions(text: string): GrammarFinding[] {
  const findings: GrammarFinding[] = [];
  for (const confusion of CONFUSIONS) {
    for (const match of text.matchAll(confusion.regex)) {
      findings.push({
        rule: confusion.title,
        title: confusion.title,
        message: confusion.message,
        excerpt: match[0],
        suggestion: matchCase(confusion.suggestion(match), match[0]),
        index: match.index,
        confidence: confusion.confidence,
      });
    }
  }
  for (const match of text.matchAll(RE_WORDS)) {
    const correction = MISSPELLINGS[match[0].toLowerCase()];
    if (correction) {
      findings.push({
        rule: "misspelling",
        title: "Likely misspelling",
        message: `"${match[0]}" → "${matchCase(correction, match[0])}".`,
        excerpt: match[0],
        suggestion: matchCase(correction, match[0]),
        index: match.index,
        confidence: "high",
      });
    }
  }
  return findings;
}

// --- Rule: capitalization -------------------------------------------------------

const RE_LOWERCASE_I = /(^|[\s(])i([\s'.,;:!?]|$)/gm;
const RE_SENTENCE_START_LOWER = /[.!?]\s+([a-z][A-Za-z]*)/g;
// Words before a period that make a following lowercase letter legitimate.
const ABBREVIATIONS = new Set([
  "al",
  "etc",
  "vs",
  "cf",
  "e.g",
  "i.e",
  "fig",
  "vol",
  "no",
  "p",
  "pp",
  "ed",
  "eds",
  "approx",
  "dept",
]);
const RE_WORD_BEFORE_PERIOD = /([A-Za-z.]+)\.$/;

function checkCapitalization(text: string): GrammarFinding[] {
  const findings: GrammarFinding[] = [];
  for (const match of text.matchAll(RE_LOWERCASE_I)) {
    findings.push({
      rule: "lowercase-i",
      title: 'Lowercase "i"',
      message: 'The pronoun "I" is always capitalized.',
      excerpt: "i",
      suggestion: "I",
      index: match.index + match[1].length,
      confidence: "high",
    });
  }
  for (const match of text.matchAll(RE_SENTENCE_START_LOWER)) {
    const before = text.slice(Math.max(0, match.index - 12), match.index + 1);
    const wordBefore = RE_WORD_BEFORE_PERIOD.exec(before);
    const stem = wordBefore?.[1].toLowerCase().replace(RE_TRAILING_DOT, "");
    if (stem && (ABBREVIATIONS.has(stem) || stem.includes("."))) {
      continue;
    }
    findings.push({
      rule: "sentence-capitalization",
      title: "Sentence starts lowercase",
      message: `Capitalize "${match[1]}" at the start of the sentence.`,
      excerpt: match[1],
      suggestion: match[1][0].toUpperCase() + match[1].slice(1),
      index: match.index + match[0].length - match[1].length,
      confidence: "medium",
    });
  }
  return findings;
}

const RE_TRAILING_DOT = /\.$/;

// --- Public API -----------------------------------------------------------------

export interface GrammarReport {
  findings: GrammarFinding[];
  wordCount: number;
}

const RE_WORD_COUNT = /\S+/g;

/** Run every rule over the text. Findings are sorted by position. */
export function checkGrammar(text: string): GrammarReport {
  const findings = [
    ...checkDoubledWords(text),
    ...checkArticles(text),
    ...checkPunctuation(text),
    ...checkConfusions(text),
    ...checkCapitalization(text),
  ].sort((a, b) => a.index - b.index);
  return {
    findings,
    wordCount: (text.match(RE_WORD_COUNT) ?? []).length,
  };
}
